import React, { useMemo, useState } from "react";
import type { Char } from "../../shared/types";
import { ABILITIES, ABILITY_NAMES, SKILLS, computed5e, signed, checkRoll, attackRoll, spellCard, type RollMode, type Ability } from "../../shared/sheet5e";
import { update } from "../net";
import { sendChat } from "../chatSend";
import { Field, uid, confirm } from "../util";

type Rep = Record<string, string>;

export function Sheet5e({ ch, editable }: { ch: Char; editable: boolean }) {
  const a = ch.attrs || {};
  const c = useMemo(() => computed5e(ch), [ch.attrs]);
  const npc = a.npc === "1";
  const mode = (a.roll_mode || "normal") as RollMode;
  const set = (k: string, v: string) => update("char", ch.id, { attrs: { [k]: v } });
  const roll = (text: string) => sendChat(text, ch.id);
  const A = (k: string, props: any = {}) => <Field value={a[k] ?? ""} onCommit={v => set(k, v)} disabled={!editable} {...props} />;

  const rep = (sec: string): Rep[] => ch.rep?.[sec] || [];
  const setRep = (sec: string, list: Rep[]) => update("char", ch.id, { rep: { [sec]: list } });

  const modeBar = (
    <div className="seg-toggle" title="How d20 rolls are made">
      {(["normal", "adv", "dis", "both"] as RollMode[]).map(m => (
        <button key={m} className={mode === m ? "on" : ""} onClick={() => set("roll_mode", m)}>
          {m === "normal" ? "Normal" : m === "adv" ? "Adv" : m === "dis" ? "Dis" : "Roll 2"}
        </button>
      ))}
    </div>
  );

  const abilityBoxes = (
    <div className="abil-row">
      {ABILITIES.map(ab => (
        <div key={ab} className="abil">
          <button className="abil-name rollable" onClick={() => roll(checkRoll(`${ABILITY_NAMES[ab]} Check`, "@{character_name}", `@{${ab}_mod}`, mode))}>{ab.toUpperCase()}</button>
          <div className="abil-mod">{signed(+c[`${ab}_mod`])}</div>
          {A(ab, { className: "abil-score", type: "number" })}
        </div>
      ))}
    </div>
  );

  const header = (
    <div className="sh-head">
      <Field className="sh-name" value={ch.name} onCommit={v => update("char", ch.id, { name: v })} disabled={!editable} />
      {modeBar}
      <label className="chk" title="NPC stat block layout">
        <input type="checkbox" checked={npc} disabled={!editable} onChange={e => set("npc", e.target.checked ? "1" : "0")} /> NPC
      </label>
    </div>
  );

  if (npc) return <div className="sheet">{header}<NpcBlock ch={ch} a={a} c={c} A={A} roll={roll} mode={mode} rep={rep} setRep={setRep} editable={editable} abilityBoxes={abilityBoxes} set={set} /></div>;

  return (
    <div className="sheet">
      {header}
      <div className="sh-meta">
        <label>Class & level {A("class_level", { placeholder: "Fighter 3" })}</label>
        <label>Level {A("level", { type: "number" })}</label>
        <label>Race {A("race")}</label>
        <label>Background {A("background")}</label>
        <label>Alignment {A("alignment")}</label>
        <label>XP {A("xp")}</label>
      </div>
      {abilityBoxes}
      <div className="sh-cols">
        <div className="sh-col">
          <div className="box">
            <div className="box-row"><span>Proficiency bonus</span><b>{signed(+c.pb)}</b></div>
            <div className="box-row">
              <label className="chk"><input type="checkbox" disabled={!editable} checked={a.inspiration === "1"} onChange={e => set("inspiration", e.target.checked ? "1" : "0")} /> Inspiration</label>
            </div>
          </div>
          <div className="box">
            <div className="box-title">Saving throws</div>
            {ABILITIES.map(ab => (
              <div key={ab} className="skill">
                <input type="checkbox" disabled={!editable} checked={a[`${ab}_save_prof`] === "1"} onChange={e => set(`${ab}_save_prof`, e.target.checked ? "1" : "0")} />
                <button className="rollable" onClick={() => roll(checkRoll(`${ABILITY_NAMES[ab]} Save`, "@{character_name}", `@{${ab}_save}`, mode))}>
                  <span className="sk-bonus">{signed(+c[`${ab}_save`])}</span> {ABILITY_NAMES[ab]}
                </button>
              </div>
            ))}
          </div>
          <div className="box">
            <div className="box-title">Skills</div>
            {SKILLS.map(([key, label, ab]) => {
              const lvl = +(a[`skill_${key}`] || 0);
              return (
                <div key={key} className="skill">
                  <button className={`prof p${lvl}`} disabled={!editable} title="Click: none → proficient → expertise" onClick={() => set(`skill_${key}`, String((lvl + 1) % 3))}>
                    {lvl === 2 ? "◆" : lvl === 1 ? "●" : "○"}
                  </button>
                  <button className="rollable" onClick={() => roll(checkRoll(label, `@{character_name} (${ab.toUpperCase()})`, `@{${key}}`, mode))}>
                    <span className="sk-bonus">{signed(+c[key])}</span> {label} <span className="dim">({ab})</span>
                  </button>
                </div>
              );
            })}
            <label className="chk small"><input type="checkbox" disabled={!editable} checked={a.jack_of_all_trades === "1"} onChange={e => set("jack_of_all_trades", e.target.checked ? "1" : "0")} /> Jack of all trades</label>
          </div>
          <div className="box">
            <div className="box-row"><span>Passive Perception</span><b>{c.passive_perception}</b></div>
            <div className="box-row"><span>Passive Investigation</span><b>{c.passive_investigation}</b></div>
            <div className="box-row"><span>Passive Insight</span><b>{c.passive_insight}</b></div>
          </div>
          <div className="box">
            <div className="box-title">Proficiencies & languages</div>
            {A("proficiencies", { multiline: true, rows: 4 })}
          </div>
        </div>

        <div className="sh-col">
          <div className="combat-row">
            <div className="stat"><div>AC</div>{A("ac", { className: "big" })}</div>
            <div className="stat">
              <button className="rollable" title="Roll initiative (adds selected/your token to the turn order)" onClick={() => roll(checkRoll("Initiative", "@{character_name}", "@{initiative}", mode === "both" ? "normal" : mode, true))}>Init</button>
              <div className="big">{signed(+c.initiative)}</div>
              {A("init_bonus", { className: "tiny", placeholder: "misc", title: "Initiative misc bonus" })}
            </div>
            <div className="stat"><div>Speed</div>{A("speed", { className: "big" })}</div>
          </div>
          <div className="box hp">
            <div className="hp-row">
              <label>HP {A("hp", { className: "big", type: "number" })}</label>
              <span className="slash">/</span>
              <label>Max {A("hp_max", { className: "big", type: "number" })}</label>
              <label>Temp {A("hp_temp", { className: "big", type: "number" })}</label>
            </div>
            <HpQuick ch={ch} editable={editable} />
            <div className="hp-row">
              <label>Hit dice {A("hit_dice", { placeholder: "3d10" })}</label>
              <button className="rollable" onClick={() => roll(`&{template:5e} {{name=Hit Die}} {{subtitle=@{character_name}}} {{Healing=[[${(a.hit_dice || "1d8").replace(/^\d+/, "1")}+@{con_mod}]]}}`)}>Roll HD</button>
            </div>
            <div className="hp-row death">
              <span>Death saves</span>
              <DeathSaves ch={ch} editable={editable} />
              <button className="rollable" onClick={() => roll(`&{template:5e} {{name=Death Save}} {{subtitle=@{character_name}}} {{Result=[[1d20]]}} {{desc=10+ succeeds · 20 regains 1 HP · 1 counts as two failures}}`)}>Roll</button>
            </div>
          </div>
          <RepSection
            title="Attacks & actions"
            list={rep("attacks")}
            setList={l => setRep("attacks", l)}
            editable={editable}
            blank={{ name: "New attack", ability: "str", prof: "1", damage: "1d6", dmg_type: "slashing" }}
            summary={r => {
              const ab = (r.ability || "str") as Ability | "spell" | "none";
              const m = ab === "none" ? 0 : ab === "spell" ? +c.spell_mod : +c[`${ab}_mod`];
              const hit = r.hit_override?.trim() ? r.hit_override : String(m + (r.prof !== "0" ? +c.pb : 0) + (+r.bonus || 0));
              return (
                <>
                  <button className="rollable grow" onClick={() => roll(attackRoll(r, mode))}>{r.name || "Attack"}</button>
                  {r.no_attack !== "1" && <span className="pill">{isNaN(+hit) ? hit : signed(+hit)}</span>}
                  <span className="pill dim">{r.damage}{r.dmg_mod === "0" || r.hit_override ? "" : m ? signed(m) : ""} {r.dmg_type}</span>
                </>
              );
            }}
            fields={[
              ["name", "Name"], ["ability", "Ability", ["str", "dex", "con", "int", "wis", "cha", "spell", "none"]], ["prof", "Proficient", ["1", "0"]], ["bonus", "Magic/misc to hit"],
              ["hit_override", "To-hit override"], ["no_attack", "No attack roll", ["0", "1"]], ["damage", "Damage (e.g. 1d8)"], ["dmg_mod", "Add ability to dmg", ["1", "0"]], ["dmg_type", "Damage type"],
              ["damage2", "Extra damage"], ["dmg_type2", "Extra type"], ["range", "Range"], ["save", "Save (e.g. DC 13 DEX)"], ["notes", "Notes", "multi"],
            ]}
          />
          <div className="box">
            <div className="box-title">Spellcasting</div>
            <div className="box-row">
              <label>Ability
                <select disabled={!editable} value={a.spell_ability || "none"} onChange={e => set("spell_ability", e.target.value)}>
                  {["none", ...ABILITIES].map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              <span>DC <b>{c.spell_dc}</b></span>
              <span>Attack <b>{signed(+c.spell_attack)}</b></span>
            </div>
            <div className="slots">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(l => (
                <div key={l} className="slot">
                  <span>{l}</span>
                  {A(`slot_${l}`, { className: "tiny", type: "number" })}
                  <span>/</span>
                  {A(`slot_${l}_max`, { className: "tiny", type: "number" })}
                </div>
              ))}
            </div>
          </div>
          <RepSection
            title="Spells"
            list={[...rep("spells")].sort((x, y) => (+x.level || 0) - (+y.level || 0) || (x.name || "").localeCompare(y.name || ""))}
            setList={l => setRep("spells", l)}
            editable={editable}
            blank={{ name: "New spell", level: "1", attack: "none" }}
            summary={r => (
              <>
                <span className="pill dim">{r.level === "0" || !r.level ? "C" : r.level}</span>
                <button className="rollable grow" onClick={() => roll(spellCard(r, mode))}>{r.name}</button>
                {r.prepared === "1" && <span className="pill">P</span>}
              </>
            )}
            fields={[
              ["name", "Name"], ["level", "Level (0 = cantrip)"], ["school", "School"], ["prepared", "Prepared", ["0", "1"]], ["cast_time", "Casting time"], ["range", "Range"],
              ["components", "Components"], ["duration", "Duration"], ["attack", "Attack/save", ["none", "attack", "save"]], ["save_ability", "Save ability"],
              ["damage", "Damage"], ["dmg_type", "Damage type"], ["heal", "Healing"], ["desc", "Description", "multi"],
            ]}
          />
        </div>

        <div className="sh-col">
          <RepSection
            title="Features & traits"
            list={rep("features")}
            setList={l => setRep("features", l)}
            editable={editable}
            blank={{ name: "New feature" }}
            summary={r => <button className="rollable grow" onClick={() => roll(`&{template:5e} {{name=${r.name}}} {{subtitle=${r.source || "@{character_name}"}}} {{desc=${r.desc || ""}}}`)}>{r.name}</button>}
            fields={[["name", "Name"], ["source", "Source"], ["uses", "Uses"], ["desc", "Description", "multi"]]}
          />
          <RepSection
            title="Inventory"
            list={rep("items")}
            setList={l => setRep("items", l)}
            editable={editable}
            blank={{ name: "Item", qty: "1" }}
            summary={r => <><span className="pill dim">{r.qty || 1}×</span><span className="grow">{r.name}</span>{r.weight && <span className="dim">{r.weight} lb</span>}</>}
            fields={[["name", "Name"], ["qty", "Quantity"], ["weight", "Weight"], ["desc", "Description", "multi"]]}
          />
          <div className="box">
            <div className="box-title">Currency</div>
            <div className="coins">
              {["cp", "sp", "ep", "gp", "pp"].map(k => <label key={k}>{k.toUpperCase()} {A(k, { className: "tiny" })}</label>)}
            </div>
            <div className="dim small">
              Weight carried: {rep("items").reduce((s, r) => s + (parseFloat(r.weight) || 0) * (parseFloat(r.qty) || 1), 0)} / {c.carry_capacity} lb
            </div>
          </div>
          <div className="box">
            <div className="box-title">Personality</div>
            <label>Traits {A("traits", { multiline: true, rows: 2 })}</label>
            <label>Ideals {A("ideals", { multiline: true, rows: 2 })}</label>
            <label>Bonds {A("bonds", { multiline: true, rows: 2 })}</label>
            <label>Flaws {A("flaws", { multiline: true, rows: 2 })}</label>
          </div>
          <div className="box">
            <div className="box-title">Senses</div>
            <label>Darkvision (ft) {A("darkvision", { className: "tiny" })}</label>
          </div>
        </div>
      </div>
    </div>
  );
}

function HpQuick({ ch, editable }: { ch: Char; editable: boolean }) {
  const [v, setV] = useState("");
  if (!editable) return null;
  const apply = (sign: 1 | -1) => {
    const n = parseInt(v);
    if (!n) return;
    const a = ch.attrs;
    let hp = parseInt(a.hp || "0"), tmp = parseInt(a.hp_temp || "0") || 0;
    const max = parseInt(a.hp_max || "0") || Infinity;
    if (sign < 0) {
      const fromTemp = Math.min(tmp, n);
      tmp -= fromTemp;
      hp -= n - fromTemp;
    } else hp = Math.min(max, hp + n);
    update("char", ch.id, { attrs: { hp: String(hp), hp_temp: tmp ? String(tmp) : "" } });
    setV("");
  };
  return (
    <div className="hp-quick">
      <input value={v} onChange={e => setV(e.target.value)} placeholder="amount" type="number" onKeyDown={e => e.key === "Enter" && apply(-1)} />
      <button onClick={() => apply(-1)} className="danger">Damage</button>
      <button onClick={() => apply(1)}>Heal</button>
    </div>
  );
}

function DeathSaves({ ch, editable }: { ch: Char; editable: boolean }) {
  const a = ch.attrs;
  const box = (k: string) => (
    <input type="checkbox" disabled={!editable} checked={a[k] === "1"} onChange={e => update("char", ch.id, { attrs: { [k]: e.target.checked ? "1" : "0" } })} />
  );
  return (
    <span className="ds">
      <span title="Successes">✔ {box("ds_s1")}{box("ds_s2")}{box("ds_s3")}</span>
      <span title="Failures">✖ {box("ds_f1")}{box("ds_f2")}{box("ds_f3")}</span>
    </span>
  );
}

type FieldDef = [string, string, (string[] | "multi")?];

export function RepSection({ title, list, setList, editable, blank, summary, fields }: {
  title: string;
  list: Rep[];
  setList: (l: Rep[]) => void;
  editable: boolean;
  blank: Rep;
  summary: (r: Rep) => React.ReactNode;
  fields: FieldDef[];
}) {
  const [open, setOpen] = useState<string | null>(null);
  const setRow = (id: string, k: string, v: string) => setList(list.map(r => (r.id === id ? { ...r, [k]: v } : r)));
  return (
    <div className="box">
      <div className="box-title">
        {title}
        {editable && <button className="mini" onClick={() => { const r = { ...blank, id: uid() }; setList([...list, r]); setOpen(r.id); }}>＋</button>}
      </div>
      {list.length === 0 && <div className="dim small">None yet.</div>}
      {list.map(r => (
        <div key={r.id} className="rep-row">
          <div className="rep-sum">
            {summary(r)}
            {editable && <button className="mini" title="Edit" onClick={() => setOpen(open === r.id ? null : r.id)}>✎</button>}
          </div>
          {open === r.id && editable && (
            <div className="rep-edit">
              {fields.map(([k, label, opt]) => (
                <label key={k} className={opt === "multi" ? "full" : ""}>
                  {label}
                  {Array.isArray(opt) ? (
                    <select value={r[k] ?? opt[0]} onChange={e => setRow(r.id, k, e.target.value)}>
                      {opt.map(o => <option key={o} value={o}>{o === "1" ? "yes" : o === "0" ? "no" : o}</option>)}
                    </select>
                  ) : (
                    <Field value={r[k] ?? ""} onCommit={v => setRow(r.id, k, v)} multiline={opt === "multi"} rows={4} />
                  )}
                </label>
              ))}
              <button className="danger mini" onClick={async () => { if (await confirm(`Delete "${r.name}"?`)) { setList(list.filter(x => x.id !== r.id)); setOpen(null); } }}>Delete</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function NpcBlock({ ch, a, c, A, roll, mode, rep, setRep, editable, abilityBoxes, set }: any) {
  return (
    <div className="npc">
      <div className="npc-top">
        <label className="grow">Size, type, alignment {A("type", { placeholder: "Medium humanoid (goblinoid), neutral evil" })}</label>
      </div>
      <div className="npc-stats">
        <label>Armor Class {A("ac", { className: "tiny" })}</label>
        <label>AC note {A("ac_note", { placeholder: "leather armor, shield" })}</label>
        <label>HP {A("hp", { className: "tiny" })} / {A("hp_max", { className: "tiny" })}</label>
        <label>HP formula {A("hp_formula", { placeholder: "2d8+2" })}</label>
        <button className="rollable" disabled={!a.hp_formula} title="Roll HP from formula and set it" onClick={async () => {
          const f = a.hp_formula;
          if (!f) return;
          // quick local estimate: average is fine for a sheet button; the roll itself goes to chat
          roll(`/gr ${f} HP for ${ch.name}`);
        }}>Roll HP</button>
        <label>Speed {A("speed")}</label>
      </div>
      {abilityBoxes}
      <div className="npc-stats">
        <label>Challenge {A("cr", { className: "tiny" })}</label>
        <label>XP {A("xp", { className: "tiny" })}</label>
        <span>PB <b>{signed(+c.pb)}</b></span>
        <button className="rollable" onClick={() => roll(checkRoll("Initiative", "@{character_name}", "@{initiative}", mode === "both" ? "normal" : mode, true))}>Init {signed(+c.initiative)}</button>
      </div>
      <div className="npc-grid">
        <label>Saving throws {A("npc_saves", { placeholder: "Dex +4, Wis +2" })}</label>
        <label>Skills {A("npc_skills", { placeholder: "Stealth +6" })}</label>
        <label>Damage resistances {A("resistances")}</label>
        <label>Damage immunities {A("immunities")}</label>
        <label>Condition immunities {A("condition_immunities")}</label>
        <label>Senses {A("senses", { placeholder: "darkvision 60 ft., passive Perception 9" })}</label>
        <label>Languages {A("languages")}</label>
        <label>Darkvision (ft, for tokens) {A("darkvision", { className: "tiny" })}</label>
      </div>
      <div className="npc-quick">
        {ABILITIES.map((ab: Ability) => (
          <button key={ab} className="rollable" onClick={() => roll(checkRoll(`${ABILITY_NAMES[ab]} Save`, "@{character_name}", `@{${ab}_save}`, mode))}>{ab.toUpperCase()} save</button>
        ))}
        <label className="chk small" title="Proficient saves (adds PB)">
          Prof saves:
          {ABILITIES.map((ab: Ability) => (
            <span key={ab}><input type="checkbox" disabled={!editable} checked={a[`${ab}_save_prof`] === "1"} onChange={e => set(`${ab}_save_prof`, e.target.checked ? "1" : "0")} />{ab}</span>
          ))}
        </label>
      </div>
      <RepSection
        title="Traits"
        list={rep("features")}
        setList={(l: Rep[]) => setRep("features", l)}
        editable={editable}
        blank={{ name: "New trait" }}
        summary={(r: Rep) => <button className="rollable grow" onClick={() => roll(`&{template:5e} {{name=${r.name}}} {{subtitle=@{character_name}}} {{desc=${r.desc || ""}}}`)}>{r.name}</button>}
        fields={[["name", "Name"], ["desc", "Description", "multi"]]}
      />
      <RepSection
        title="Actions"
        list={rep("attacks")}
        setList={(l: Rep[]) => setRep("attacks", l)}
        editable={editable}
        blank={{ name: "Scimitar", hit_override: "4", damage: "1d6+2", dmg_type: "slashing", range: "reach 5 ft." }}
        summary={(r: Rep) => (
          <>
            <button className="rollable grow" onClick={() => roll(attackRoll(r, mode))}>{r.name}</button>
            {r.no_attack !== "1" && <span className="pill">{r.hit_override ? signed(+r.hit_override || 0) : "auto"}</span>}
            {r.damage && <span className="pill dim">{r.damage} {r.dmg_type}</span>}
          </>
        )}
        fields={[
          ["name", "Name"], ["hit_override", "To-hit bonus (e.g. 4)"], ["no_attack", "No attack roll", ["0", "1"]], ["ability", "Ability (if no override)", ["str", "dex", "con", "int", "wis", "cha", "none"]],
          ["damage", "Damage (full, e.g. 1d6+2)"], ["dmg_type", "Damage type"], ["damage2", "Extra damage"], ["dmg_type2", "Extra type"], ["range", "Reach/range"], ["save", "Save"], ["notes", "Description", "multi"],
        ]}
      />
      <RepSection
        title="Legendary & reactions"
        list={rep("legendary")}
        setList={(l: Rep[]) => setRep("legendary", l)}
        editable={editable}
        blank={{ name: "Legendary action" }}
        summary={(r: Rep) => <button className="rollable grow" onClick={() => roll(`&{template:5e} {{name=${r.name}}} {{subtitle=@{character_name}}} {{desc=${r.desc || ""}}}`)}>{r.name}</button>}
        fields={[["name", "Name"], ["desc", "Description", "multi"]]}
      />
    </div>
  );
}
